import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';

import { useError } from '@/modules/utils/custom-hooks/use-error';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { Modal } from '@/ui-components/modal/modal';

import { useDocumentStatusesInit } from '../hooks/use-document-statuses-init';
import { DocumentStatusList } from './components/document-status-list';
import { NewStatusRow } from './components/new-status-row';

export const DocumentStatusModal: FunctionComponent = () => {
	const t = useTranslations();
	const { getSearchParams } = useNavigation();
	const { isDarkTheme } = useTheme();
	const { setError } = useError();
	const { id: projectId } = getSearchParams();
	const { documentStatuses } = useDocumentStatusesInit(projectId);
	const [statusState, setStatusState] = useState<{ isNew: boolean; currentId: string | null }>({ isNew: false, currentId: null });
	const [newStatus, setNewStatus] = useState<{ name: string; color: string }>({ name: '', color: isDarkTheme ? '#f1f1f1' : '#333333' });

	const handleStatusState = (data: Partial<typeof statusState>) => {
		setStatusState((prev) => ({ ...prev, ...data }));
	};

	const clearStatusState = () => {
		setStatusState({ isNew: false, currentId: null });
		setNewStatus({ name: '', color: '#333333' });
		setError({ message: null, details: {} });
	};

	return (
		<Modal name='DocumentStatus' title={{ text: t('T_MANAGE_STATUS'), variant: 'h5' }} dismissible closeButton className='relative'>
			<div className='max-h-[350px] min-w-[500px] max-w-[500px] overflow-hidden'>
				<div className='absolute top-[64px] right-9'>
					<Button
						disabled={statusState.isNew || !!statusState.currentId}
						sizing='small'
						onClick={() => {
							handleStatusState({ isNew: true });
						}}
						variant='primary'
					>
						{`+ ${t('T_ADD_STATUS')}`}
					</Button>
				</div>

				<DocumentStatusList
					documentStatuses={documentStatuses}
					statusState={statusState}
					handleStatusState={handleStatusState}
					clearStatusState={clearStatusState}
				/>
				<NewStatusRow
					projectId={projectId}
					clearStatusState={clearStatusState}
					newStatus={newStatus}
					setNewStatus={setNewStatus}
					statusState={statusState}
				/>
			</div>
		</Modal>
	);
};
